import React from 'react';
import { Option } from 'src/components/Configure/ConfigureModal';
import { GridMeasureDefn, State } from 'src/pages/AssortmentBuild/FlowSheet/FlowSheet.types';
import { companionDataParse, convertPivotItemsToRowData } from 'src/pages/AssortmentBuild/FlowSheet/FlowSheet.utils';
import { listPairStyle } from 'src/components/ListGridPair/ListGridPair.styles';
import Overlay from 'src/common-ui/components/Overlay/Overlay';
import Subheader from 'src/components/Subheader/Subheader.container';
import CompanionList from 'src/components/CompanionList/CompanionList';
import { CompanionListProps } from 'src/components/CompanionList/CompanionList.types';
import { CompanionView } from 'configuration-schemas/viewdefns/general';
import type { CompanionDataLookup } from 'src/utils/Component/ListView';
import { PricingGrid, PricingGridProps } from 'src/pages/AssortmentBuild/Pricing/PricingGrid';
import { isNil, forEach, isEmpty, isEqual } from 'lodash';
import { MassEdit, MassEditSubmissionData } from 'src/components/MassEdit/MassEdit';
import { ASSORTMENT_BUILD_FILTER_WARNING, CACHED_DATA_EDITING_DISABLED_WARNING } from 'src/utils/Domain/Constants';
import { FabType } from 'src/components/higherOrder/withFab';

import { PricingOverTimeProps } from './PricingOverTime.types';
import type { ViewConfiguratorModalProps } from 'src/components/ViewConfiguratorModal/ViewConfiguratorModal.types';
import { TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { LegacySubheaderActionButtonProps, SortByDirection } from 'src/components/Subheader/Subheader.types';

import { GranularEditPayloadItem } from 'src/dao/pivotClient';

export class PricingOverTime extends React.Component<PricingOverTimeProps, State> {
  private _isUnMounted = false;
  protected pGrid!: PricingGrid | null;

  constructor(props: PricingOverTimeProps) {
    super(props);

    this.state = {
      configureIsOpen: false,
      companionData: [],
      uniqueItems: props.dataLoaded ? props.uniqueItems : [],
      rowData: [],
    };
  }

  static getDerivedStateFromProps(newProps: PricingOverTimeProps, currentState: State): State {
    return {
      ...currentState,
      uniqueItems: newProps.dataLoaded ? newProps.uniqueItems : [],
    };
  }

  componentDidMount() {
    if (this._isUnMounted) {
      return;
    }
    this.props.onShowView();
  }

  componentDidUpdate(prevProps: PricingOverTimeProps, prevState: State) {
    const { overTimeData, viewDefns } = this.props;

    if (!isEqual(prevProps.overTimeData, overTimeData) || !isEqual(prevProps.viewDefns, viewDefns)) {
      const updatedData = convertPivotItemsToRowData(
        overTimeData,
        viewDefns.grid.view as GridMeasureDefn[],
        ['addoff', 'eo', 'eff_aur', 'corpaddoff', 'corpexcl', 'cccurp', 'ccdiscountpct'],
        'stylecolor',
        'week',
      );

      this.setState({
        rowData: updatedData,
      });
    }

    // All of the `componentDidUpdate` checks are for companion view,
    // but if we're not using the companionView, then we should skip them all
    // initial data loading is done in `onShowView`
    if (this.props.hideCompanion) {
      return;
    }

    if (
      !isEqual(this.state.uniqueItems, prevState.uniqueItems) ||
      !isEqual(prevProps.companionSortField, this.props.companionSortField) ||
      !isEqual(prevProps.companionSortDirection, this.props.companionSortDirection) ||
      !isEqual(prevProps.companionLevelField, this.props.companionLevelField)
    ) {
      this.rebuildCompanionData();
    }
  }
  updateConfigureSelections(selections: Option[]) {
    if (this.props.updateConfigureSelections) {
      this.props.updateConfigureSelections(selections);
    }
  }

  componentWillUnmount() {
    this._isUnMounted = true;
    if (this.props.onDestroy) {
      this.props.onDestroy();
    }
  }

  rebuildCompanionData() {
    if (!this.props.dataLoaded) {
      return;
    }
    const companionData = companionDataParse(
      this.state.uniqueItems,
      this.props.companionDataLookup,
      this.props.companionSortDirection,
      this.props.companionSortField,
      this.props.companionLevelField ?? undefined,
    );

    this.setState(
      {
        companionData,
      },
      () => {
        if (isEmpty(companionData)) {
          this.onCompanionItemClick('');
          this.setState({
            rowData: [],
          });
        } else {
          const selectedIndex = companionData.findIndex((datum) => datum.id === this.props.selectedId);
          if (selectedIndex === -1) {
            this.onCompanionItemClick(companionData[0].id);
          }
        }
      },
    );
  }

  onFabClick = () => {
    switch (this.props.fab?.fabType) {
      case FabType.planning:
        this.props.updateAssortmentPlan();
        break;
      default:
        break;
    }
  };

  /*  Start Companion List Events */

  onCompanionItemClick = (itemId: string) => {
    if (this.props.onCompanionItemClick) {
      this.props.onCompanionItemClick(itemId);
    }
  };

  onCompanionDirectionChange = (newDir: SortByDirection) => {
    if (this._isUnMounted) return;
    this.props.updateCompanionSortDirection(newDir);
  };

  onCompanionSortChange = (sortByDataIndex: string) => {
    if (this._isUnMounted) return;
    this.props.updateCompanionSortField(sortByDataIndex);
  };

  onCompanionLevelChange = (dataIndex: string) => {
    if (this._isUnMounted) {
      return;
    }
    this.props.updateCompanionLevelField(dataIndex);
  };

  onCompanionCollapseToggle = () => {
    if (this._isUnMounted) {
      return;
    }

    this.props.onToggleCompanion();
  };

  /* End Companion List Events */

  onGridValueChange = (payload: GranularEditPayloadItem[]) => {
    // on grid value changes, don't want a data refresh, so pass false
    this.props.submitPayload(payload, false);
  };

  handleMassEditSubmission = (submission: MassEditSubmissionData) => {
    const { selectedModifier, selectedItems, selectedWeekList, modifierValue, extraModifierValues } = submission;

    if (!isNil(selectedModifier) && !isNil(selectedWeekList)) {
      // no null submission data expected, except for modifier value
      const updatedData: GranularEditPayloadItem[] = [];
      forEach(selectedWeekList, (weekNumber) => {
        const updatedWeekData = selectedItems.map((item) => {
          return {
            coordinates: {
              product: item.value, // styleColor id
              time: weekNumber,
            },
            [selectedModifier]: modifierValue,
            // additional modifiers here
            ...extraModifierValues,
          };
        });

        updatedData.push(...updatedWeekData);
      });

      if (this.props.submitPayload) {
        this.props.submitPayload(updatedData);
      }
    }
  };
  onUpdateConfig = (config: TenantConfigViewData) => {
    this.props.onUpdateConfig(config);
  };

  generateExtraActionButtonProps(): LegacySubheaderActionButtonProps {
    const { massEditDataLoaded, massEditData, allowFrom, allowTo, rangeList, scopeStart, viewDefns } = this.props;
    const { massEdit: massEditConfig } = viewDefns;
    const configViewItems = MassEdit.getConfigViewItems(massEditConfig);
    const itemsDataIndex = !isEmpty(configViewItems) ? configViewItems[1].dataIndex : '';
    const editableItems =
      massEditData &&
      massEditData.flat.map((dataItem) => {
        const label = `${dataItem['name']}-${dataItem[itemsDataIndex]}`;
        return {
          value: dataItem['id'],
          label,
        };
      });

    const massEdit = isNil(massEditConfig)
      ? undefined
      : {
          config: massEditConfig,
          allowFrom,
          allowTo,
          scopeStart: scopeStart || '',
          rangeList,
          editableItems,
          title: 'Mass Copy Pricing Events',
          handleSubmit: this.handleMassEditSubmission,
          dataLoading: !massEditDataLoaded,
        };

    return {
      massEdit,
    };
  }

  render() {
    const {
      title,
      configLoaded,
      dataLoaded,
      overTimeDataLoaded,
      liveDataReady,
      isPrintMode,
      viewDataStates,
      showFlowStatus,
      onItemClicked,
      selectedId,
      rowHeight,
      groupRowHeight,
      columnWidth,
      showUndoBtn,
      isCompanionCollapsed,
      hideCompanion,
    } = this.props;

    const sortOptions = dataLoaded ? this.props.sortOptions : [];
    const companionDataLookup = dataLoaded ? this.props.companionDataLookup : undefined;
    const companionData = this.state.companionData;

    const levelOptions = this.props.viewDefns?.listLevelBy?.view;
    const savedLevelField = this.props.companionLevelField;
    const defaultLevelDataIndex =
      !isNil(savedLevelField) && levelOptions?.some((opt: any) => opt.dataIndex === savedLevelField)
        ? savedLevelField
        : (levelOptions?.[0]?.dataIndex ?? '');

    // process companion
    let companion = <></>;
    if (!hideCompanion && !isPrintMode) {
      // TODO fix `defaultCompanionSortField` here
      const loadedProps = this.props as any;
      const companionProps: CompanionListProps = {
        config: {
          sortBy: { options: sortOptions, defaults: loadedProps.defaultCompanionSortField },
          itemMappings: { main: loadedProps.companionDataItemMapping as CompanionView['itemMappings']['main'] },
          levelBy: levelOptions
            ? ({ defaults: { dataIndex: defaultLevelDataIndex }, view: levelOptions } as CompanionView['levelBy'])
            : undefined,
        },
        selectedItemId: selectedId ?? null,
        sortField: this.props.companionSortField ?? loadedProps.defaultCompanionSortField?.dataIndex ?? undefined,
        levelField: this.props.companionLevelField ?? undefined,
        data: companionData,
        isDataloaded: dataLoaded,
        dataLookup: companionDataLookup as CompanionDataLookup,
        sortDirection: this.props.companionSortDirection,
        isCompanionCollapsed,
        onToggleCompanion: this.onCompanionCollapseToggle,
        onSortDirectionChange: this.onCompanionDirectionChange,
        onSortChange: this.onCompanionSortChange,
        onLevelChange: this.onCompanionLevelChange,
        renderSearchComponent: false,
        renderFilterComponent: false,
        onItemSelect: this.onCompanionItemClick,
      };
      companion = <CompanionList {...companionProps} />;
    }

    const pricingGridProps: PricingGridProps = {
      timeEntries: this.props.timeInfo.entries,
      rowData: this.state.rowData,
      rowHeight: this.props.dataLoaded ? rowHeight : 30,
      anchorField: this.props.scopeStart || this.props.timeInfo.planCurrent,
      events: this.props.events,
      editable: liveDataReady,
      onValueChange: this.onGridValueChange,
      onItemClicked,
      groupRowHeight,
      columnWidth: columnWidth,
      loaded: overTimeDataLoaded,
      adornments: this.props.adornments,
    };

    const viewConfigurator: ViewConfiguratorModalProps | undefined =
      configLoaded && this.props.configuratorViewDefn && this.props.unmodifiedViewDefn
        ? {
            viewConfig: this.props.configuratorViewDefn,
            unmodifiedViewDefn: this.props.unmodifiedViewDefn,
            updateConfig: this.onUpdateConfig,
            showPinCheckboxForGrid: false,
            companionData: {
              companionSortDirection: this.props.companionSortDirection,
              companionSortField: this.props.companionSortField,
            },
            defaultCompanionData: {
              companionSortDirection: 'desc',
              companionSortField: this.props.defaultCompanionSortField.dataIndex,
            },
          }
        : undefined;

    // configured error text takes precedence over hardcoded
    let errorCondition: string = ASSORTMENT_BUILD_FILTER_WARNING;
    if (!liveDataReady) {
      errorCondition = CACHED_DATA_EDITING_DISABLED_WARNING;
    } else if (this.props.subheaderErrorText) {
      errorCondition = this.props.subheaderErrorText;
    }

    return (
      <div className={listPairStyle}>
        <Overlay type="loading" visible={!configLoaded} />
        <Subheader
          title={title || ''}
          errorCondition={errorCondition}
          showSearch={true}
          showFlowStatus={showFlowStatus}
          showUndoBtn={showUndoBtn}
          summary={this.props.dataLoaded ? this.props.subheaderSummary : undefined}
          viewDataState={viewDataStates}
          viewConfigurator={viewConfigurator}
          extraLegacyActionButtons={this.generateExtraActionButtonProps()}
        />
        <div className="data-container">
          {companion}
          <PricingGrid ref={(el) => (this.pGrid = el)} {...pricingGridProps} />
        </div>
      </div>
    );
  }
}
